import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import PublicSite from "./PublicSite";
import PageMetadata from "./seo/PageMetadata";
import {
  cachePublicTranscriptionCount,
  fetchPublicTranscriptionCount,
  readCachedPublicTranscriptionCount,
} from "./landingMetrics";
import { queuePageView, trackButtonClick } from "./firebase";
import "./App.css";

const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));

const scheduleWhenIdle = (callback, fallbackDelay = 900) => {
  if (typeof window === "undefined") {
    callback();
    return () => undefined;
  }

  if (typeof window.requestIdleCallback === "function") {
    const callbackId = window.requestIdleCallback(callback, {
      timeout: fallbackDelay * 2,
    });

    return () => window.cancelIdleCallback(callbackId);
  }

  const timeoutId = window.setTimeout(callback, fallbackDelay);
  return () => window.clearTimeout(timeoutId);
};

const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}${location.hash}`;

    queuePageView(currentPath);

    return undefined;
  }, [location.hash, location.pathname, location.search]);

  return null;
};

const getButtonLabel = (buttonElement) => {
  const fallbackText = buttonElement.textContent
    ? buttonElement.textContent.replace(/\s+/g, " ").trim()
    : "";

  const rawLabel =
    buttonElement.dataset.analyticsLabel ||
    buttonElement.getAttribute("aria-label") ||
    fallbackText ||
    buttonElement.id ||
    "unnamed_button";

  return rawLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "unnamed_button";
};

const ButtonClickTracker = () => {
  useEffect(() => {
    const handleClick = (event) => {
      const buttonElement = event.target.closest("button, a.btn, [role='button']");

      if (!buttonElement) {
        return;
      }

      const buttonTarget =
        buttonElement.getAttribute("href") ||
        buttonElement.dataset.target ||
        window.location.pathname;

      trackButtonClick({
        buttonName: getButtonLabel(buttonElement),
        buttonLocation: window.location.pathname,
        buttonTarget,
      });
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  return null;
};

const AppRoutes = ({ isTranscriptionCountLoading, transcriptionCount }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname === "/admin" || location.pathname.startsWith("/admin/");

  return (
    <>
      <PageViewTracker />
      <ButtonClickTracker />
      <PageMetadata />
      {isAdminRoute ? <Routes>
        <Route
          path="/admin/*"
          element={
            <Suspense fallback={<div className="admin-route-loading">Loading admin dashboard...</div>}>
              <AdminDashboard />
            </Suspense>
          }
        />
      </Routes> : <PublicSite isTranscriptionCountLoading={isTranscriptionCountLoading} transcriptionCount={transcriptionCount} />}
    </>
  );
};

const AppContent = () => {
  const location = useLocation();
  const [cachedTranscriptionCount] = useState(() =>
    readCachedPublicTranscriptionCount()
  );
  const [transcriptionCountState, setTranscriptionCountState] = useState({
    isLoading: cachedTranscriptionCount === null,
    value: cachedTranscriptionCount,
  });

  useEffect(() => {
    // The live homepage counter is not needed on articles, legal pages or admin.
    if (location.pathname !== "/") return undefined;
    let isActive = true;
    let cleanupScheduledLoad = () => undefined;

    const loadPublicMetrics = async () => {
      try {
        const totalTranscriptions = await fetchPublicTranscriptionCount();

        if (!isActive) {
          return;
        }

        cachePublicTranscriptionCount(totalTranscriptions);
        setTranscriptionCountState({
          isLoading: false,
          value: totalTranscriptions,
        });
      } catch {
        if (!isActive) {
          return;
        }

        setTranscriptionCountState({
          isLoading: false,
          value:
            cachedTranscriptionCount === null
              ? null
              : cachedTranscriptionCount,
        });
      }
    };

    if (cachedTranscriptionCount === null) {
      loadPublicMetrics();
    } else {
      cleanupScheduledLoad = scheduleWhenIdle(() => {
        loadPublicMetrics();
      }, 2200);
    }

    return () => {
      isActive = false;
      cleanupScheduledLoad();
    };
  }, [cachedTranscriptionCount, location.pathname]);

  return (
      <AppRoutes
        isTranscriptionCountLoading={transcriptionCountState.isLoading}
        transcriptionCount={transcriptionCountState.value}
      />
  );
};

const App = () => <Router><AppContent /></Router>;

export default App;
